import { calculateBruttoAmount, calculateNettoAmount } from "@/lib/utils";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { v4 as uuidv4 } from "uuid";

import type { LineItem } from "@/apis/gql/types";
import { useBookingTextStore } from "@/queries/fastlane/get-review";
import { InvoiceModes } from "@/types/utils";

import { type OperationAction, OperationType } from "./types";

enum ActionType {
  ADD_LINE_ITEM = "ADD_LINE_ITEM",
  UPDATE_LINE_ITEM = "UPDATE_LINE_ITEM",
  DELETE_LINE_ITEM = "DELETE_LINE_ITEM",
  SET_LINE_ITEMS = "SET_LINE_ITEMS",
  UPDATE_AMOUNT = "UPDATE_AMOUNT",
  UPDATE_ACCOUNT_ID = "UPDATE_ACCOUNT_ID",
  UPDATE_TAX_CODE_ID = "UPDATE_TAX_CODE_ID",
  UPDATE_COST_CENTER_ID = "UPDATE_COST_CENTER_ID",
  UPDATE_VAT_RATE = "UPDATE_VAT_RATE",
  SET_OPERATIONS = "SET_OPERATIONS",
  UNLINK = "UNLINK",
}

type LineItemAction =
  | {
      type: ActionType.ADD_LINE_ITEM;
      payload: { lineItemId: string; lineItem: Partial<LineItem> };
    }
  | {
      type: ActionType.UPDATE_LINE_ITEM;
      payload: { id: string; updates: Partial<LineItem> };
    }
  | { type: ActionType.DELETE_LINE_ITEM; payload: string[] }
  | { type: ActionType.SET_LINE_ITEMS; payload: LineItem[] }
  | {
      type: ActionType.UPDATE_AMOUNT;
      payload: { lineItemIds: string[]; amount: number };
    }
  | {
      type: ActionType.UPDATE_ACCOUNT_ID;
      payload: {
        lineItemIds: string[];
        accountId: string | null;
        vatRate: number | undefined;
        invoiceMode: InvoiceModes;
      };
    }
  | {
      type: ActionType.UPDATE_TAX_CODE_ID;
      payload: {
        lineItemIds: string[];
        taxCodeId: string | null;
        vatRate: number;
        invoiceMode: InvoiceModes;
      };
    }
  | {
      type: ActionType.UPDATE_VAT_RATE;
      payload: { lineItemIds: string[]; vatRate: number };
    }
  | {
      type: ActionType.UPDATE_COST_CENTER_ID;
      payload: { lineItemIds: string[]; costCenterId: string | null };
    }
  | { type: ActionType.SET_OPERATIONS; payload: OperationAction[] }
  | { type: ActionType.UNLINK; payload: string[] };

type State = {
  lineItems: LineItem[];
  operations: OperationAction[];
};

function reducer(state: State, action: LineItemAction): State {
  switch (action.type) {
    case ActionType.ADD_LINE_ITEM:
      return {
        ...state,
        lineItems: [...state.lineItems, action.payload.lineItem as LineItem],
        operations: [
          ...state.operations,
          {
            type: OperationType.CREATE,
            lineItemId: action.payload.lineItemId,
            lineItem: action.payload.lineItem as LineItem,
            timestamp: new Date(),
          },
        ],
      };
    case ActionType.UPDATE_LINE_ITEM: {
      const result = state.lineItems.reduce<{
        lineItems: LineItem[];
        operations: OperationAction[];
      }>(
        (acc, item: LineItem) => {
          if (item.lineItemId === action.payload.id) {
            acc.lineItems.push({
              ...item,
              ...action.payload.updates,
            } as LineItem);
            acc.operations.push({
              type: OperationType.UPDATE,
              lineItemId: item.lineItemId,
              changes: action.payload.updates,
              timestamp: new Date(),
            });
          } else {
            acc.lineItems.push(item);
          }
          return acc;
        },
        {
          lineItems: [],
          operations: [],
        },
      );

      return {
        ...state,
        lineItems: result.lineItems,
        operations: [...state.operations, ...result.operations],
      };
    }
    case ActionType.DELETE_LINE_ITEM: {
      const result = state.lineItems.reduce<{
        lineItems: LineItem[];
        operations: OperationAction[];
      }>(
        (acc, item) => {
          if (action.payload.includes(item.lineItemId)) {
            acc.lineItems.push({
              ...item,
              deletedAt: new Date(),
            });
            acc.operations.push({
              type: OperationType.DELETE,
              lineItemId: item.lineItemId,
              timestamp: new Date(),
            });
          } else {
            acc.lineItems.push(item);
          }
          return acc;
        },
        {
          lineItems: [],
          operations: [],
        },
      );

      return {
        ...state,
        lineItems: result.lineItems,
        operations: [...state.operations, ...result.operations],
      };
    }
    case ActionType.SET_LINE_ITEMS:
      return {
        ...state,
        lineItems: action.payload,
      };
    case ActionType.UPDATE_AMOUNT: {
      const lineItem = state.lineItems.find((item) =>
        action.payload.lineItemIds.includes(item.lineItemId),
      );

      if (!lineItem) {
        return state;
      }

      const stateWithoutLineItemIds = state.lineItems.reduce<{
        lineItems: LineItem[];
        operations: OperationAction[];
      }>(
        (acc, item) => {
          if (action.payload.lineItemIds.includes(item.lineItemId)) {
            acc.lineItems.push({
              ...item,
              deletedAt: new Date(),
            });
            acc.operations.push({
              type: OperationType.DELETE,
              lineItemId: item.lineItemId,
              timestamp: new Date(),
            });
          } else {
            acc.lineItems.push(item);
          }
          return acc;
        },
        {
          lineItems: [],
          operations: [],
        },
      );

      const newLineItem = {
        ...lineItem,
        lineItemId: uuidv4(),
        amount: action.payload.amount,
        name: lineItem?.bookingText ?? "",
        description: lineItem?.bookingText ?? "",
        nettoAmount: calculateNettoAmount(
          action.payload.amount,
          lineItem?.vatRate ?? 0,
        ),
      } as LineItem;

      // This is not suggested, to be done here since it breaks purity of the reducer.
      // But the other scenario, handling it in the dispatch function would create unnecessary re-renders. Ill fix it some day.
      try {
        useBookingTextStore.getState().setBookingText(newLineItem.lineItemId, {
          lineItemIds: [newLineItem.lineItemId],
          bookingText: newLineItem.bookingText ?? "",
        });
      } catch (error) {}

      const operation = {
        type: OperationType.CREATE,
        lineItemId: newLineItem.lineItemId,
        lineItem: newLineItem,
      } as OperationAction;

      return {
        ...state,
        lineItems: [...stateWithoutLineItemIds.lineItems, newLineItem],
        operations: [...stateWithoutLineItemIds.operations, operation],
      };
    }
    case ActionType.UPDATE_ACCOUNT_ID: {
      const result = state.lineItems.reduce<{
        lineItems: LineItem[];
        operations: OperationAction[];
      }>(
        (acc, item) => {
          const {
            vatRate: _vatRate,
            lineItemIds,
            accountId,
            invoiceMode,
          } = action.payload;

          let vatRate = item.vatRate;
          let nettoAmount = item.nettoAmount;
          let amount = item.amount;
          if (_vatRate !== undefined) {
            vatRate = _vatRate;
          }

          // this swap only happens when there is an automatic account change
          if (invoiceMode === InvoiceModes.Netto) {
            amount = calculateBruttoAmount(item.nettoAmount ?? 0, vatRate ?? 0);
            nettoAmount = item.nettoAmount;
          }

          if (lineItemIds.includes(item.lineItemId)) {
            acc.lineItems.push({
              ...item,
              accountId,
              amount,
              nettoAmount,
              vatRate,
            });
            acc.operations.push({
              type: OperationType.UPDATE,
              lineItemId: item.lineItemId,
              changes: { accountId, nettoAmount, amount, vatRate },
              timestamp: new Date(),
            });
          } else {
            acc.lineItems.push(item);
          }
          return acc;
        },
        {
          lineItems: [],
          operations: [],
        },
      );

      return {
        ...state,
        lineItems: result.lineItems,
        operations: [...state.operations, ...result.operations],
      };
    }
    case ActionType.UPDATE_TAX_CODE_ID: {
      const result = state.lineItems.reduce<{
        lineItems: LineItem[];
        operations: OperationAction[];
      }>(
        (acc, item) => {
          const { vatRate, lineItemIds, taxCodeId, invoiceMode } =
            action.payload;

          let amount = item.amount;
          let nettoAmount = item.nettoAmount;

          if (invoiceMode === InvoiceModes.Netto) {
            amount = calculateBruttoAmount(item.nettoAmount ?? 0, vatRate ?? 0);
            nettoAmount = item.nettoAmount;
          }

          if (lineItemIds.includes(item.lineItemId)) {
            acc.lineItems.push({
              ...item,
              taxCodeId,
              vatRate,
              amount,
              nettoAmount,
            });
            acc.operations.push({
              type: OperationType.UPDATE,
              lineItemId: item.lineItemId,
              changes: {
                taxCodeId,
                vatRate,
                amount,
                nettoAmount,
              },
              timestamp: new Date(),
            });
          } else {
            acc.lineItems.push(item);
          }
          return acc;
        },
        {
          lineItems: [],
          operations: [],
        },
      );

      return {
        ...state,
        lineItems: result.lineItems,
        operations: [...state.operations, ...result.operations],
      };
    }
    case ActionType.UPDATE_COST_CENTER_ID: {
      const result = state.lineItems.reduce<{
        lineItems: LineItem[];
        operations: OperationAction[];
      }>(
        (acc, item) => {
          if (action.payload.lineItemIds.includes(item.lineItemId)) {
            acc.lineItems.push({
              ...item,
              costCenterId: action.payload.costCenterId,
            });
            acc.operations.push({
              type: OperationType.UPDATE,
              lineItemId: item.lineItemId,
              changes: { costCenterId: action.payload.costCenterId },
              timestamp: new Date(),
            });
          } else {
            acc.lineItems.push(item);
          }
          return acc;
        },
        {
          lineItems: [],
          operations: [],
        },
      );

      return {
        ...state,
        lineItems: result.lineItems,
        operations: [...state.operations, ...result.operations],
      };
    }
    case ActionType.UPDATE_VAT_RATE: {
      const result = state.lineItems.reduce<{
        lineItems: LineItem[];
        operations: OperationAction[];
      }>(
        (acc, item) => {
          if (action.payload.lineItemIds.includes(item.lineItemId)) {
            acc.lineItems.push({
              ...item,
              vatRate: action.payload.vatRate,
            });
            acc.operations.push({
              type: OperationType.UPDATE,
              lineItemId: item.lineItemId,
              changes: { vatRate: action.payload.vatRate },
              timestamp: new Date(),
            });
          } else {
            acc.lineItems.push(item);
          }
          return acc;
        },
        {
          lineItems: [],
          operations: [],
        },
      );

      return {
        ...state,
        lineItems: result.lineItems,
        operations: [...state.operations, ...result.operations],
      };
    }
    case ActionType.SET_OPERATIONS: {
      return {
        ...state,
        operations: action.payload,
      };
    }
    case ActionType.UNLINK: {
      const result = state.lineItems.reduce<{
        lineItems: LineItem[];
        operations: OperationAction[];
      }>(
        (acc, item) => {
          const lineItemIds = action.payload;

          const unlinked = uuidv4();

          if (lineItemIds.includes(item.lineItemId)) {
            acc.lineItems.push({
              ...item,
              unlinked,
            });
            acc.operations.push({
              type: OperationType.UPDATE,
              lineItemId: item.lineItemId,
              changes: {
                unlinked,
              },
              timestamp: new Date(),
            });
          } else {
            acc.lineItems.push(item);
          }
          return acc;
        },
        {
          lineItems: [],
          operations: [],
        },
      );

      return {
        ...state,
        lineItems: result.lineItems,
        operations: [...state.operations, ...result.operations],
      };
    }
    default:
      return state;
  }
}

export const useBookingsReducer = () => {
  const [lineItemState, dispatch] = useReducer(reducer, {
    lineItems: [],
    operations: [],
  });

  const stateRef = useRef<State>({
    lineItems: [],
    operations: [],
  });

  useEffect(() => {
    stateRef.current = lineItemState;
  }, [lineItemState]);

  const handleAddLineItem = (lineItem: Partial<LineItem>) => {
    dispatch({
      type: ActionType.ADD_LINE_ITEM,
      payload: { lineItemId: uuidv4(), lineItem },
    });
  };

  const handleUpdateBookingAmount = useCallback(
    (lineItemIds: string[], amount: number) => {
      dispatch({
        type: ActionType.UPDATE_AMOUNT,
        payload: { lineItemIds, amount },
      });
    },
    [],
  );

  const handleUpdateBookingNettoAmount = useCallback(
    (lineItemIds: string[], amount: number) => {
      dispatch({
        type: ActionType.UPDATE_AMOUNT,
        payload: { lineItemIds, amount },
      });
    },
    [],
  );

  const handleUpdateAccountId = useCallback(
    (
      lineItemIds: string[],
      accountId: string | null,
      vatRate: number | undefined,
      invoiceMode: InvoiceModes,
    ) => {
      dispatch({
        type: ActionType.UPDATE_ACCOUNT_ID,
        payload: { lineItemIds, accountId, vatRate, invoiceMode },
      });
    },
    [],
  );

  const handleUpdateTaxCodeId = useCallback(
    (
      lineItemIds: string[],
      taxCodeId: string | null,
      vatRate: number,
      invoiceMode: InvoiceModes,
    ) => {
      dispatch({
        type: ActionType.UPDATE_TAX_CODE_ID,
        payload: { lineItemIds, taxCodeId, vatRate, invoiceMode },
      });
    },
    [],
  );

  const handleUpdateBookingVatRate = useCallback(
    (lineItemIds: string[], vatRate: number) => {
      dispatch({
        type: ActionType.UPDATE_VAT_RATE,
        payload: { lineItemIds, vatRate },
      });
    },
    [],
  );

  const handleDeleteLineItem = useCallback((lineItemIds: string[]) => {
    dispatch({
      type: ActionType.DELETE_LINE_ITEM,
      payload: lineItemIds,
    });
  }, []);

  const handleUnlink = useCallback((lineItemIds: string[]) => {
    dispatch({
      type: ActionType.UNLINK,
      payload: lineItemIds,
    });
  }, []);

  const handleSetLineItems = useCallback((lineItems: LineItem[]) => {
    dispatch({
      type: ActionType.SET_LINE_ITEMS,
      payload: lineItems,
    });
  }, []);

  const handleSetOperations = useCallback((operations: OperationAction[]) => {
    dispatch({
      type: ActionType.SET_OPERATIONS,
      payload: operations,
    });
  }, []);

  const handleUpdateLineItems = useCallback(
    (lineItemIds: string[], lineItem: Partial<LineItem>) => {
      lineItemIds.forEach((id) => {
        dispatch({
          type: ActionType.UPDATE_LINE_ITEM,
          payload: { id, updates: lineItem },
        });
      });
    },
    [],
  );

  const handleUpdateCostCenterId = useCallback(
    (lineItemIds: string[], costCenterId: string | null) => {
      dispatch({
        type: ActionType.UPDATE_COST_CENTER_ID,
        payload: { lineItemIds, costCenterId },
      });
    },
    [],
  );

  const getOperations = useCallback(() => {
    return stateRef.current.operations;
  }, []);

  const getLineItems = useCallback(() => {
    return stateRef.current.lineItems;
  }, []);

  return {
    lineItemState,
    dispatch,
    handleAddLineItem,
    handleUpdateBookingAmount,
    handleUpdateBookingNettoAmount,
    handleUpdateAccountId,
    handleUpdateTaxCodeId,
    handleUpdateCostCenterId,
    handleUpdateBookingVatRate,
    handleDeleteLineItem,
    handleSetLineItems,
    handleSetOperations,
    handleUpdateLineItems,
    handleUnlink,
    getOperations,
    getLineItems,
  };
};
